import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, getDocs, doc, setDoc, updateDoc, deleteDoc, serverTimestamp, query, orderBy, getDoc, where, limit } from 'firebase/firestore';
import Swal from 'sweetalert2';
import AdminLayout from './AdminLayout';
import DeviceOnlineBadge from './DeviceOnlineBadge';
import SetupGuard from '../components/SetupGuard';
import './AdminAccount.css';

const KDSStationsPage = () => {
  const params = useParams();
  const [locationId, setLocationId] = useState(params.locationId);
  const navigate = useNavigate();
  const auth = getAuth();

  const [user, setUser] = useState(null);
  const [stations, setStations] = useState([]);
  const [workzones, setWorkzones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingStation, setEditingStation] = useState(null);

  // Sessions modal state
  const [showSessionsModal, setShowSessionsModal] = useState(false);
  const [selectedStationForSessions, setSelectedStationForSessions] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionLogs, setSessionLogs] = useState([]);
  const [sessionLoading, setSessionLoading] = useState(false);

  // Form state
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formWorkzoneId, setFormWorkzoneId] = useState('');
  const [formViewType, setFormViewType] = useState('columns');
  const [formStatus, setFormStatus] = useState('active');
  const [formQueryWindowHours, setFormQueryWindowHours] = useState(24);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        if (!locationId) {
          const db = getFirestore();
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const selectedId = userDoc.data().selectedLocationId;
            if (selectedId) {
              setLocationId(selectedId);
            } else {
              Swal.fire('Info', 'Please select a location first', 'info');
              navigate('/admin/locations');
              return;
            }
          }
        }
        if (locationId) {
          await loadData(currentUser.uid);
        }
      } else {
        navigate('/admin/login');
      }
    });
    return () => unsubscribe();
  }, [locationId]);

  const loadData = async (uid) => {
    try {
      setLoading(true);
      const db = getFirestore();

      // Load stations for this location
      const stationSnap = await getDocs(
        query(
          collection(db, 'users', uid, 'kds'),
          where('locationId', '==', locationId),
          orderBy('createdAt', 'desc')
        )
      );
      const stationData = stationSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setStations(stationData);

      // Load workzones for dropdown
      const wzSnap = await getDocs(
        query(collection(db, 'users', uid, 'locations', locationId, 'workzones'), orderBy('order'))
      );
      setWorkzones(wzSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error loading KDS stations:', error);
      Swal.fire('Error', 'Failed to load KDS stations', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!user || !locationId) return;
    if (!formName.trim()) {
      Swal.fire('Error', 'Station name is required', 'error');
      return;
    }
    if (!formWorkzoneId) {
      Swal.fire('Error', 'Please select a workzone', 'error');
      return;
    }

    try {
      const db = getFirestore();
      if (editingStation) {
        await updateDoc(doc(db, 'users', user.uid, 'kds', editingStation.id), {
          name: formName.trim(),
          description: formDescription.trim(),
          workzoneId: formWorkzoneId,
          locationId,
          viewType: formViewType,
          status: formStatus,
          queryWindowHours: formQueryWindowHours,
          updatedAt: serverTimestamp(),
        });
      } else {
        await setDoc(doc(collection(db, 'users', user.uid, 'kds')), {
          name: formName.trim(),
          description: formDescription.trim(),
          workzoneId: formWorkzoneId,
          locationId,
          viewType: formViewType,
          status: formStatus,
          queryWindowHours: formQueryWindowHours,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
      setShowModal(false);
      setEditingStation(null);
      clearForm();
      await loadData(user.uid);
      window.dispatchEvent(new CustomEvent('setupGuideRefresh'));
      Swal.fire('Success', `Station ${editingStation ? 'updated' : 'created'}`, 'success');
    } catch (error) {
      console.error('Error saving KDS station:', error);
      Swal.fire('Error', 'Failed to save station', 'error');
    }
  };

  const handlePairDevice = async (station) => {
    if (!user) return;
    const { value: code } = await Swal.fire({
      title: 'Pair Physical Device',
      input: 'text',
      inputLabel: 'Enter the activation code displayed on the device',
      inputPlaceholder: 'e.g. ABC123',
      showCancelButton: true,
      confirmButtonText: 'Pair',
      confirmButtonColor: '#28a745',
      cancelButtonColor: '#6c757d',
    });
    if (!code) return;

    try {
      setLoading(true);
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();
      const callable = httpsCallable(functions, 'pairDeviceByActivationCode');
      const result = await callable({
        activationCode: code.trim().toUpperCase(),
        deviceType: 'kds',
        linkedDocId: station.id,
      });
      if (result.data.success) {
        await loadData(user.uid);
        Swal.fire('Paired', 'Device paired successfully', 'success');
      }
    } catch (error) {
      console.error('Error pairing device:', error);
      Swal.fire('Error', error.message || 'Failed to pair device', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUnpairDevice = async (station) => {
    if (!user) return;
    const result = await Swal.fire({
      title: 'Unpair Physical Device?',
      text: `This will release the physical device from "${station.name}".`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Unpair',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;

    try {
      setLoading(true);
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();
      const callable = httpsCallable(functions, 'unpairDevice');
      await callable({
        deviceType: 'kds',
        linkedDocId: station.id,
      });
      await loadData(user.uid);
      Swal.fire('Unpaired', 'Physical device has been released', 'success');
    } catch (error) {
      console.error('Error unpairing device:', error);
      Swal.fire('Error', error.message || 'Failed to unpair device', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (station) => {
    const result = await Swal.fire({
      title: 'Delete Station?',
      text: `Are you sure you want to delete "${station.name}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Delete',
    });

    if (result.isConfirmed && user) {
      try {
        const db = getFirestore();
        await deleteDoc(doc(db, 'users', user.uid, 'kds', station.id));
        await loadData(user.uid);
        window.dispatchEvent(new CustomEvent('setupGuideRefresh'));
        Swal.fire('Deleted', 'Station deleted', 'success');
      } catch (error) {
        console.error('Error deleting station:', error);
        Swal.fire('Error', 'Failed to delete station', 'error');
      }
    }
  };

  const handleLoadSessions = async (station) => {
    if (!user || !station.locationId) return;
    setSessionLoading(true);
    setSelectedStationForSessions(station);
    setSelectedSession(null);
    setSessionLogs([]);
    try {
      const db = getFirestore();
      const q = query(
        collection(db, 'users', user.uid, 'locations', station.locationId, 'kdsSessions'),
        where('stationId', '==', station.id),
        orderBy('openedAt', 'desc'),
        limit(10)
      );
      const snap = await getDocs(q);
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setSessions(data);
      setShowSessionsModal(true);
    } catch (error) {
      console.error('Error loading sessions:', error);
      Swal.fire('Error', 'Failed to load sessions', 'error');
    } finally {
      setSessionLoading(false);
    }
  };

  const handleSelectSession = async (session) => {
    if (!user || !selectedStationForSessions?.locationId) return;
    setSessionLoading(true);
    setSelectedSession(session);
    try {
      const db = getFirestore();
      const q = query(
        collection(db, 'users', user.uid, 'locations', selectedStationForSessions.locationId, 'kdsSessions', session.id, 'completedLogs'),
        orderBy('completedAt', 'desc')
      );
      const snap = await getDocs(q);
      const logs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setSessionLogs(logs);
    } catch (error) {
      console.error('Error loading completed logs:', error);
      Swal.fire('Error', 'Failed to load session details', 'error');
    } finally {
      setSessionLoading(false);
    }
  };

  const computeSessionStats = (session, logs) => {
    const orderCount = logs.length;
    const itemCount = logs.reduce((sum, log) => {
      return sum + (log.items || []).reduce((itemSum, it) => itemSum + (it.qty || 1), 0);
    }, 0);
    const itemFreq = {};
    logs.forEach(log => {
      (log.items || []).forEach(it => {
        const name = it.productName || it.name || 'Item';
        itemFreq[name] = (itemFreq[name] || 0) + (it.qty || 1);
      });
    });
    const topItem = Object.entries(itemFreq).sort((a, b) => b[1] - a[1])[0];
    let avgTime = null;
    if (session.openedAt && logs.length > 0) {
      const opened = session.openedAt.toDate ? session.openedAt.toDate() : new Date(session.openedAt);
      const lastCompleted = logs[0].completedAt?.toDate ? logs[0].completedAt.toDate() : new Date(logs[0].completedAt);
      const diffMs = lastCompleted.getTime() - opened.getTime();
      const diffMin = Math.round(diffMs / 60000);
      avgTime = diffMin > 0 ? Math.round(diffMin / logs.length) : 0;
    }
    return { orderCount, itemCount, topItem: topItem ? topItem[0] : '-', avgTime };
  };

  const clearForm = () => {
    setFormName('');
    setFormDescription('');
    setFormWorkzoneId('');
    setFormViewType('columns');
    setFormStatus('active');
    setFormQueryWindowHours(12);
  };

  const openAddModal = () => {
    setEditingStation(null);
    clearForm();
    setShowModal(true);
  };

  const openEditModal = (station) => {
    setEditingStation(station);
    setFormName(station.name || '');
    setFormDescription(station.description || '');
    setFormWorkzoneId(station.workzoneId || '');
    setFormViewType(station.viewType || 'columns');
    setFormStatus(station.status || 'active');
    setFormQueryWindowHours(station.queryWindowHours || 12);
    setShowModal(true);
  };

  const getWorkzoneName = (id) => {
    const wz = workzones.find(w => w.id === id);
    return wz?.name || 'Unknown';
  };

  if (loading) {
    return (
      <AdminLayout title="Kitchen Displays">
        <div className="text-center py-5">
          <img src="/fodyfee_loading.png" alt="Loading..." style={{ width: 80, height: 80, objectFit: 'contain' }} />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Kitchen Displays">
      <SetupGuard prerequisites={['location']}>
      <div className="admin-account-page">
        {/* Add Button */}
        <div className="row mb-3">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="m-0">Manage Kitchen Display Stations</h5>
              <button className="btn btn-primary" onClick={openAddModal}>
                <i className="fas fa-plus mr-2"></i>Add Station
              </button>
            </div>
          </div>
        </div>

        {/* Stations List */}
        <div className="row">
          {stations.length === 0 ? (
            <div className="col-12 text-center py-5">
              <i className="fas fa-tv fa-4x text-muted mb-3"></i>
              <h5 className="text-muted">No KDS stations yet</h5>
              <p className="text-muted">Click "Add Station" to create your first kitchen display station</p>
            </div>
          ) : (
            stations.map(station => (
              <div key={station.id} className="col-md-6 col-lg-4 mb-3">
                <div className="card">
                  <div className="card-body d-flex align-items-center">
                    <div
                      className="d-flex align-items-center justify-content-center mr-3"
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: 10,
                        background: station.status === 'active' ? 'rgba(255,107,53,0.1)' : 'rgba(108,117,125,0.1)',
                        color: station.status === 'active' ? '#ff6b35' : '#6c757d',
                      }}
                    >
                      <i className="fas fa-tv"></i>
                    </div>
                    <div className="flex-grow-1">
                      <h6 className="m-0 font-weight-bold">{station.name}</h6>
                        <small className="text-muted">
                          {station.viewType === 'columns' ? 'Columns' : 'Grid'} · {getWorkzoneName(station.workzoneId)} · {station.queryWindowHours || 24}h window
                        </small>
                      <div>
                        <span
                          className={`badge badge-${station.status === 'active' ? 'success' : 'secondary'}`}
                          style={{ fontSize: '0.7rem', marginTop: 4 }}
                        >
                          {station.status === 'active' ? 'Active' : 'Inactive'}
                        </span>
                        <DeviceOnlineBadge deviceId={station.fodyfeeDeviceId} />
                      </div>
                    </div>
                    <div className="d-flex align-items-center">
                      <button
                        className="btn btn-sm btn-success mr-2"
                        style={{ borderRadius: 8, fontSize: '0.8rem' }}
                        onClick={() => navigate(`/admin/kds-view/${station.id}`)}
                        title="Open KDS View"
                      >
                        <i className="fas fa-tv mr-1"></i>View
                      </button>
                      <div className="position-relative">
                        <button
                          className="btn btn-sm btn-link"
                          onClick={() => setOpenDropdownId(openDropdownId === station.id ? null : station.id)}
                        >
                          <i className="fas fa-ellipsis-v"></i>
                        </button>
                        {openDropdownId === station.id && (
                          <div
                            className="card position-absolute"
                            style={{
                              right: 0,
                              top: '100%',
                              zIndex: 1000,
                              minWidth: '120px',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                            }}
                          >
                            <div className="list-group list-group-flush">
                              <button className="list-group-item list-group-item-action py-2" onClick={() => { setOpenDropdownId(null); openEditModal(station); }}>
                                <i className="fas fa-edit text-primary mr-2"></i>Edit
                              </button>
                              <button className="list-group-item list-group-item-action py-2" onClick={() => { setOpenDropdownId(null); handleLoadSessions(station); }}>
                                <i className="fas fa-history text-secondary mr-2"></i>Sessions
                              </button>
                              {!station.fodyfeeDeviceId && (
                                <button className="list-group-item list-group-item-action py-2" onClick={() => { setOpenDropdownId(null); handlePairDevice(station); }}>
                                  <i className="fas fa-mobile-alt text-info mr-2"></i>Pair Physical Device
                                </button>
                              )}
                              {station.fodyfeeDeviceId && (
                                <button className="list-group-item list-group-item-action py-2 text-danger" onClick={() => { setOpenDropdownId(null); handleUnpairDevice(station); }}>
                                  <i className="fas fa-unlink mr-2"></i>Unpair Device
                                </button>
                              )}
                              <button className="list-group-item list-group-item-action py-2 text-danger" onClick={() => { setOpenDropdownId(null); handleDelete(station); }}>
                                <i className="fas fa-trash-alt mr-2"></i>Delete
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title font-weight-bold">{editingStation ? 'Edit Station' : 'Add Station'}</h5>
                <button type="button" className="close" onClick={() => { setShowModal(false); setEditingStation(null); }}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>Station Name <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    placeholder="e.g., Grill Station Display"
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    className="form-control"
                    rows={2}
                    value={formDescription}
                    onChange={(e) => setFormDescription(e.target.value)}
                    placeholder="Optional description"
                  />
                </div>
                <div className="form-group">
                  <label>Workzone <span className="text-danger">*</span></label>
                  <select className="form-control" value={formWorkzoneId} onChange={(e) => setFormWorkzoneId(e.target.value)}>
                    <option value="">Select a workzone...</option>
                    {workzones.map(wz => (
                      <option key={wz.id} value={wz.id}>{wz.name}</option>
                    ))}
                  </select>
                  {workzones.length === 0 && (
                    <small className="text-muted">No workzones found. Please add workzones first.</small>
                  )}
                </div>
                <div className="form-group">
                  <label>View Type</label>
                  <select className="form-control" value={formViewType} onChange={(e) => setFormViewType(e.target.value)}>
                    <option value="columns">Columns</option>
                    <option value="grid">Grid</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Query Window (hours)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={formQueryWindowHours}
                    min={1}
                    onChange={(e) =>     setFormQueryWindowHours(Math.max(1, parseInt(e.target.value) || 12))}
                  />
                  <small className="text-muted">How far back to show orders (default: 12 hours)</small>
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select className="form-control" value={formStatus} onChange={(e) => setFormStatus(e.target.value)}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => { setShowModal(false); setEditingStation(null); }}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" style={{ backgroundColor: '#ff6b35', borderColor: '#ff6b35' }} onClick={handleSave}>
                  {editingStation ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sessions Modal */}
      {showSessionsModal && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title font-weight-bold">
                  {selectedSession ? `Session: ${selectedSession.employeeName || 'Unknown'} · ${selectedSession.openedAt ? new Date(selectedSession.openedAt.toDate ? selectedSession.openedAt.toDate() : selectedSession.openedAt).toLocaleString() : 'N/A'}` : `Sessions — ${selectedStationForSessions?.name || ''}`}
                </h5>
                <button type="button" className="close" onClick={() => {
                  if (selectedSession) { setSelectedSession(null); setSessionLogs([]); }
                  else { setShowSessionsModal(false); setSessions([]); setSelectedStationForSessions(null); }
                }}>
                  <span>{selectedSession ? '← Back' : '×'}</span>
                </button>
              </div>
              <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                {sessionLoading ? (
                  <div className="text-center py-4"><div className="spinner-border" role="status"></div></div>
                ) : selectedSession ? (
                  <div>
                    {(() => {
                      const stats = computeSessionStats(selectedSession, sessionLogs);
                      return (
                        <div className="mb-3">
                          <div className="row text-center mb-3">
                            <div className="col-6 col-md-3 mb-2">
                              <div className="card p-2">
                                <div className="text-muted" style={{ fontSize: '0.75rem' }}>Orders Completed</div>
                                <div className="font-weight-bold" style={{ fontSize: '1.25rem' }}>{stats.orderCount}</div>
                              </div>
                            </div>
                            <div className="col-6 col-md-3 mb-2">
                              <div className="card p-2">
                                <div className="text-muted" style={{ fontSize: '0.75rem' }}>Items Completed</div>
                                <div className="font-weight-bold" style={{ fontSize: '1.25rem' }}>{stats.itemCount}</div>
                              </div>
                            </div>
                            <div className="col-6 col-md-3 mb-2">
                              <div className="card p-2">
                                <div className="text-muted" style={{ fontSize: '0.75rem' }}>Top Item</div>
                                <div className="font-weight-bold text-truncate" style={{ fontSize: '1rem' }} title={stats.topItem}>{stats.topItem}</div>
                              </div>
                            </div>
                            <div className="col-6 col-md-3 mb-2">
                              <div className="card p-2">
                                <div className="text-muted" style={{ fontSize: '0.75rem' }}>Avg Time (min)</div>
                                <div className="font-weight-bold" style={{ fontSize: '1.25rem' }}>{stats.avgTime != null ? stats.avgTime : '—'}</div>
                              </div>
                            </div>
                          </div>
                          <h6 className="font-weight-bold mb-2">Completed Orders</h6>
                          {sessionLogs.length === 0 ? (
                            <p className="text-muted">No completed orders in this session.</p>
                          ) : (
                            <div className="list-group">
                              {sessionLogs.map(log => (
                                <div key={log.id} className="list-group-item list-group-item-action flex-column align-items-start py-2">
                                  <div className="d-flex w-100 justify-content-between">
                                    <h6 className="mb-1 font-weight-bold">#{log.saleNumber || log.saleId?.substring(0, 8)}</h6>
                                    <small className="text-muted">
                                      {log.completedAt ? new Date(log.completedAt.toDate ? log.completedAt.toDate() : log.completedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                                    </small>
                                  </div>
                                  <p className="mb-1 text-muted" style={{ fontSize: '0.85rem' }}>{log.customerName || 'Walk-in'} · {log.items?.length || 0} items</p>
                                  <div>
                                    {(log.items || []).map((it, idx) => (
                                      <span key={idx} className="badge badge-light mr-1" style={{ fontSize: '0.7rem' }}>
                                        {it.qty || 1}x {it.productName || it.name || 'Item'}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  <div>
                    {sessions.length === 0 ? (
                      <p className="text-muted text-center py-4">No sessions found for this station.</p>
                    ) : (
                      <div className="list-group">
                        {sessions.map(sess => (
                          <button
                            key={sess.id}
                            className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                            onClick={() => handleSelectSession(sess)}
                          >
                            <div>
                              <div className="font-weight-bold">{sess.employeeName || 'Unknown Employee'}</div>
                              <small className="text-muted">
                                {sess.openedAt ? new Date(sess.openedAt.toDate ? sess.openedAt.toDate() : sess.openedAt).toLocaleString() : 'N/A'}
                                {' · '}{sess.status || 'closed'}
                                {' · Session '}{sess.id.slice(-5)}
                              </small>
                            </div>
                            <i className="fas fa-chevron-right text-muted"></i>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      </SetupGuard>
    </AdminLayout>
  );
};

export default KDSStationsPage;
